/**
 * `/liturgies/{id}/selection-policy` — what is offered here, and who may choose it.
 *
 * From the mockup's `isPolicy` block: conflicts banner first when present, then Roles and
 * assignments, then per-Item policy cards with **Offer to Participant** checklists; aside carries
 * Policy summary, Where authority comes from, and Policy history.
 *
 * The screen edits a *ruleset*, not the effective configuration. Unchecking an option adds it to
 * this Liturgy's withheld list; it does not delete anything from the Template, which still holds
 * every option the Rite permits. That separation is the whole reason this screen exists rather than
 * an "edit template" button (docs/engineering/06-selection-policy.md).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { AccessMode, PolicyRule } from '@pius/contracts';

import { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import { attributionFor } from '../planning/renderers';
import {
  fetchLiturgyPolicy,
  saveLiturgyPolicy,
  type PolicyChangeEntry,
  type PolicyHistoryEntry,
  type PolicyItem,
  type PolicyRole,
  type PolicySummary,
} from './api';
import './policies.css';

const MODES: { value: AccessMode; key: string }[] = [
  { value: 'selectable', key: 'policies.editor.mode_selectable' },
  { value: 'read_only', key: 'policies.editor.mode_admin' },
  { value: 'hidden', key: 'policies.editor.mode_hidden' },
];

export function SelectionPolicyScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/selection-policy' });
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const policy = useQuery({
    queryKey: ['selection-policy', id],
    queryFn: () => fetchLiturgyPolicy(id),
  });

  const [edited, setEdited] = useState<PolicyRule[] | null>(null);

  const rules = useMemo(
    () => edited ?? rulesFrom(policy.data?.items ?? [], policy.data?.roles ?? []),
    [edited, policy.data],
  );

  const save = useMutation({
    mutationFn: () =>
      saveLiturgyPolicy(id, {
        // The revision the screen loaded. The server refuses if it has moved on.
        expected_revision: policy.data?.policy_revision ?? 0,
        rules,
      }),
    onSuccess: async () => {
      setEdited(null);
      await queryClient.invalidateQueries({ queryKey: ['selection-policy', id] });
      await queryClient.invalidateQueries({ queryKey: ['planning', id] });
    },
  });

  if (policy.isPending) return <p className="pol-state">{t('policies.library.loading')}</p>;

  if (policy.isError) {
    const error = policy.error as ApiError;
    return (
      <div className="pol">
        <Notice referenceId={error.correlationId}>{error.message}</Notice>
      </div>
    );
  }

  const view = policy.data;
  const dirty = edited !== null;
  const saveError = save.error instanceof ApiError ? save.error : undefined;

  const update = (
    rule: PolicyRule | null,
    target: { kind: 'item' | 'role'; key: string },
  ): void => {
    const without = rules.filter(
      (r) => !(r.target_kind === target.kind && r.target_key === target.key),
    );
    setEdited(rule ? [...without, rule] : without);
  };

  const ruleFor = (kind: 'item' | 'role', key: string): PolicyRule | undefined =>
    rules.find((r) => r.target_kind === kind && r.target_key === key);

  return (
    <div className="pol">
      <header className="pol__head">
        <div>
          <h1 className="pol__title">{t('policies.editor.title')}</h1>
          <p className="pol__lede">{t('policies.editor.lede')}</p>
          <Link className="pol__back" to="/liturgies/$id/planning" params={{ id }}>
            ← {view.title}
          </Link>
        </div>
        <div className="pol__ident">
          <div className="pol__path">/liturgies/{id}/selection-policy</div>
          <div className="pol__rev">policy revision {view.policy_revision}</div>
        </div>
      </header>

      <WorkspaceTabs liturgyId={id} />

      {saveError ? (
        <Notice referenceId={saveError.correlationId}>{saveError.message}</Notice>
      ) : null}

      {view.conflicts.length > 0 ? <ConflictBanner conflicts={view.conflicts} /> : null}

      <div className="pol__grid">
        <div className="pol__main">
          <RolesPanel roles={view.roles} ruleFor={ruleFor} onChange={update} liturgyId={id} />

          {view.items.map((item) => (
            <ItemPolicyCard
              key={item.item_key}
              item={item}
              rule={ruleFor('item', item.item_key)}
              onChange={(rule) => update(rule, { kind: 'item', key: item.item_key })}
            />
          ))}
        </div>

        <aside className="pol__aside">
          <div className="pol__card">
            <p className="pol__eyebrow">{t('policies.editor.policy_summary')}</p>
            <SummaryList summary={view.summary} />
          </div>

          <div className="pol__card pol__card--warm">
            <p className="pol__eyebrow">{t('policies.editor.where_authority')}</p>
            <ol className="pol__layers">
              {(
                t('policies.editor.layers', {
                  returnObjects: true,
                  defaultValue: [],
                }) as { label: string; note: string }[]
              ).map((layer) => (
                <li key={layer.label}>
                  <span className="pol__layer-dot" />
                  <div>
                    <div className="pol__layer-label">{layer.label}</div>
                    <div className="pol__layer-note">{layer.note}</div>
                  </div>
                </li>
              ))}
            </ol>
            <p className="pol__fine">
              No policy layer can make a Rite-invalid option valid. A later layer may only restore
              what an earlier preference excluded.
            </p>
          </div>

          <div className="pol__card">
            <p className="pol__eyebrow">{t('policies.editor.policy_history')}</p>
            <HistoryList history={view.history} />
            <p className="pol__fine">
              Recorded against the Administrator Capability that made the change — never the raw
              credential. Concurrent edits to the same revision are rejected rather than merged.
            </p>
          </div>
        </aside>
      </div>

      <div className={`pol__bar${dirty ? ' pol__bar--dirty' : ''}`}>
        <span className="pol__bar-text">
          {dirty ? t('policies.editor.unsaved_changes') : t('policies.editor.no_unsaved')}
        </span>
        <button
          type="button"
          className="pol__btn pol__btn--ghost"
          disabled={!dirty || save.isPending}
          onClick={() => setEdited(null)}
        >
          {t('policies.editor.discard')}
        </button>
        <button
          type="button"
          className="pol__btn"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('policies.editor.saving') : t('policies.editor.save_policy')}
        </button>
      </div>
    </div>
  );
}

// ── conflicts ───────────────────────────────────────────────────────────────

function ConflictBanner({
  conflicts,
}: {
  conflicts: { id: string; item_label: string; withheld_value: string }[];
}): React.JSX.Element {
  return (
    <div className="pol__conflicts">
      <p className="pol__conflicts-head">
        {conflicts.length === 1 ? '1 conflict' : `${conflicts.length} conflicts`}
      </p>
      <ul>
        {conflicts.map((conflict) => (
          <li key={conflict.id}>
            <span className="pol__conflict-item">{conflict.item_label}</span>
            <span className="pol__conflict-value">
              “{conflict.withheld_value}” is no longer offered
            </span>
          </li>
        ))}
      </ul>
      <p className="pol__fine">
        No selection is ever silently deleted or substituted. An Administrator restores the option,
        chooses another permitted value, or requests Participant revision.
      </p>
    </div>
  );
}

// ── roles ───────────────────────────────────────────────────────────────────

function RolesPanel({
  roles,
  ruleFor,
  onChange,
  liturgyId,
}: {
  roles: PolicyRole[];
  ruleFor: (kind: 'item' | 'role', key: string) => PolicyRule | undefined;
  onChange: (rule: PolicyRule | null, target: { kind: 'item' | 'role'; key: string }) => void;
  liturgyId: string;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <section className="pol__panel">
      <div className="pol__panel-head">
        <div>
          <h2 className="pol__panel-title">{t('policies.editor.roles_title')}</h2>
          <p className="pol__panel-sub">{t('policies.editor.roles_sub')}</p>
        </div>
        <Link className="pol__link" to="/liturgies/$id" params={{ id: liturgyId }}>
          {t('policies.editor.open_overview')}
        </Link>
      </div>

      {roles.map((role) => {
        const rule = ruleFor('role', role.role_key);
        const target = { kind: 'role' as const, key: role.role_key };

        return (
          <div
            key={role.role_key}
            className={`pol__role${role.in_use ? '' : ' pol__role--withdrawn'}`}
          >
            <div className="pol__role-name">{role.label}</div>

            <span
              className={`pol__origin pol__origin--${role.is_constraint ? 'constraint' : 'template'}`}
            >
              {role.is_constraint ? t('people.origin_constraint') : t('people.origin_template')}
            </span>

            <ModeToggle
              current={role.access_mode}
              onSet={(mode) => onChange(withMode(rule, target, mode), target)}
            />

            {/* **A Constraint Role can be narrowed but never withdrawn.** The control is absent
                rather than disabled: offering a button that always refuses is worse than not
                offering one. The server refuses it too, whatever the client sends. */}
            {role.is_constraint ? (
              <span className="pol__role-fixed">{t('policies.editor.required_fixed')}</span>
            ) : (
              <button
                type="button"
                className="pol__link pol__link--button"
                onClick={() =>
                  onChange({ ...(rule ?? blank(target)), withdrawn: role.in_use }, target)
                }
              >
                {role.in_use ? t('policies.editor.withdraw') : t('policies.editor.put_back')}
              </button>
            )}
          </div>
        );
      })}

      <p className="pol__panel-note">{t('policies.editor.roles_note')}</p>
    </section>
  );
}

// ── items ───────────────────────────────────────────────────────────────────

function ItemPolicyCard({
  item,
  rule,
  onChange,
}: {
  item: PolicyItem;
  rule: PolicyRule | undefined;
  onChange: (rule: PolicyRule | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const target = { kind: 'item' as const, key: item.item_key };

  const toggleOption = (key: string, offered: boolean): void => {
    const base = rule ?? blank(target);
    const withheld = new Set(base.withheld_option_keys ?? []);
    const restored = new Set(base.offered_option_keys ?? []);

    if (offered) {
      // Unchecking withholds it *here*. The Template is untouched.
      withheld.add(key);
      restored.delete(key);
    } else {
      withheld.delete(key);
      // Only meaningful when an earlier layer withheld it — an explicit restore, without which
      // exclusions could only ever accumulate down the chain.
      restored.add(key);
    }

    onChange({
      ...base,
      withheld_option_keys: [...withheld],
      offered_option_keys: [...restored],
    });
  };

  return (
    <section className="pol__panel">
      <div className="pol__panel-head">
        <div>
          <h2 className="pol__panel-title">{item.label}</h2>
          <p className="pol__panel-sub">{item.section_label}</p>
        </div>

        {item.overridden ? (
          <button
            type="button"
            className="pol__link pol__link--button"
            onClick={() => onChange(null)}
          >
            {t('policies.editor.reset_inherited')}
          </button>
        ) : null}

        <ModeToggle
          current={item.access_mode}
          onSet={(mode) => onChange(withMode(rule, target, mode))}
        />
      </div>

      {item.conflicts.length > 0 ? (
        <p className="pol__panel-conflict">
          An approved value on this item is no longer offered:{' '}
          {item.conflicts
            .map((key) => item.options.find((o) => o.key === key)?.label ?? key)
            .join(', ')}
          . It has not been changed.
        </p>
      ) : null}

      {/* Attribution beside a mode set elsewhere. A parish can lift its own exclusion, not the
          diocese's, and needs to be able to tell which it is looking at. */}
      {item.access_decided_by !== 'template' && !item.overridden ? (
        <p className="pol__panel-inherited">
          This access mode comes from the {item.access_decided_by} layer.
        </p>
      ) : null}

      {item.has_options ? (
        <div className="pol__options">
          <p className="pol__eyebrow pol__eyebrow--quiet">
            {t('policies.editor.offer_participant')}
          </p>
          {item.options.map((option) => {
            const attribution = attributionFor(option);
            const conflicted = item.conflicts.includes(option.key);
            // Retired in the Template: gone from practice, and no Policy can bring it back.
            const locked = option.decided_by === 'rite' && !option.offered;

            return (
              <label
                key={option.key}
                className={`pol__option${option.offered ? '' : ' pol__option--off'}`}
              >
                <input
                  type="checkbox"
                  checked={option.offered}
                  disabled={locked}
                  onChange={() => toggleOption(option.key, option.offered)}
                />
                <span className="pol__option-label">{option.label}</span>
                {conflicted ? (
                  <span className="pol__option-conflict">
                    {t('policies.editor.approved_conflict')}
                  </span>
                ) : attribution ? (
                  <span className="pol__option-source">{attribution}</span>
                ) : null}
              </label>
            );
          })}
        </div>
      ) : (
        <p className="pol__panel-note">{t('policies.editor.no_bounded_options')}</p>
      )}
    </section>
  );
}

function ModeToggle({
  current,
  onSet,
}: {
  current: AccessMode;
  onSet: (mode: AccessMode) => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <div className="pol__modes" role="group" aria-label="Access mode">
      {MODES.map((mode) => (
        <button
          key={mode.value}
          type="button"
          aria-pressed={current === mode.value}
          className={`pol__mode${current === mode.value ? ' pol__mode--on' : ''}`}
          onClick={() => onSet(mode.value)}
        >
          {t(mode.key)}
        </button>
      ))}
    </div>
  );
}

// ── aside ───────────────────────────────────────────────────────────────────

function SummaryList({ summary }: { summary: PolicySummary }): React.JSX.Element {
  const rows = [
    { n: summary.items, label: 'items in this template version' },
    { n: summary.narrowed_items, label: 'items with options withheld' },
    { n: summary.withheld_options, label: 'options withheld in total' },
    { n: summary.participant_restricted_items, label: 'items participants may not set' },
    { n: summary.withdrawn_roles, label: 'roles withdrawn' },
  ];

  return (
    <ul className="pol__summary">
      {rows.map((row) => (
        <li key={row.label}>
          <span className="pol__summary-n">{row.n}</span>
          <span className="pol__summary-label">{row.label}</span>
        </li>
      ))}
    </ul>
  );
}

function HistoryList({ history }: { history: PolicyHistoryEntry[] }): React.JSX.Element {
  const { t } = useTranslation('common');
  if (history.length === 0) {
    return <p className="pol__fine">{t('policies.editor.no_history')}</p>;
  }

  return (
    <ul className="pol__history">
      {history.map((entry) => (
        <li key={entry.revision}>
          <span className="pol__history-when">
            r{entry.revision} · {entry.occurred_at.slice(0, 10)}
          </span>
          <span>
            {actorPhrase(entry)} {describe(entry.entries)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Who made a change, in words.
 *
 * A Capability is described by what it is, never named — there is no person behind it to name, and
 * inventing one would misrepresent an anonymous administrator as an identified one.
 */
function actorPhrase(entry: PolicyHistoryEntry): string {
  switch (entry.actor_kind) {
    case 'account':
      return entry.actor_display_name ?? 'An account';
    case 'capability':
      return 'An Administrator Capability';
    default:
      return 'The system';
  }
}

/** History as decisions. A revision that changed nothing says so rather than printing an empty list. */
function describe(entries: PolicyChangeEntry[]): string {
  if (entries.length === 0) return 'saved the policy with no effective change.';

  const withheld = entries.reduce(
    (total, entry) => total + entry.options.filter((o) => o.to === 'withheld').length,
    0,
  );
  const restored = entries.reduce(
    (total, entry) => total + entry.options.filter((o) => o.to === 'offered').length,
    0,
  );
  const modes = entries.filter((entry) => entry.access_mode !== undefined).length;
  const withdrawals = entries.filter((entry) => entry.withdrawn !== undefined).length;

  const parts = [
    withheld > 0 ? `withheld ${withheld} ${plural(withheld, 'option')}` : null,
    restored > 0 ? `restored ${restored} ${plural(restored, 'option')}` : null,
    modes > 0 ? `changed access on ${modes} ${plural(modes, 'target')}` : null,
    withdrawals > 0 ? `changed use of ${withdrawals} ${plural(withdrawals, 'role')}` : null,
  ].filter((part): part is string => part !== null);

  return `${parts.join(', ')}.`;
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

// ── rule helpers ────────────────────────────────────────────────────────────

function blank(target: { kind: 'item' | 'role'; key: string }): PolicyRule {
  return { target_kind: target.kind, target_key: target.key };
}

function withMode(
  rule: PolicyRule | undefined,
  target: { kind: 'item' | 'role'; key: string },
  mode: AccessMode,
): PolicyRule {
  return { ...(rule ?? blank(target)), access_mode: mode };
}

/**
 * The ruleset the screen starts from, reconstructed from the resolved view.
 *
 * **Only this Liturgy's own decisions are reconstructed** — an `overridden` flag marks them. A
 * restriction inherited from an organization is visible on screen but is not copied into this
 * layer's ruleset, because copying it would silently make it this parish's own decision and survive
 * the diocese lifting it.
 */
function rulesFrom(items: PolicyItem[], roles: PolicyRole[]): PolicyRule[] {
  const rules: PolicyRule[] = [];

  for (const item of items) {
    if (!item.overridden) continue;

    const withheld = item.options.filter((o) => !o.offered && o.decided_by === 'liturgy');
    const restored = item.options.filter((o) => o.offered && o.decided_by === 'liturgy');

    rules.push({
      target_kind: 'item',
      target_key: item.item_key,
      ...(item.access_decided_by === 'liturgy' ? { access_mode: item.access_mode } : {}),
      ...(withheld.length > 0 ? { withheld_option_keys: withheld.map((o) => o.key) } : {}),
      ...(restored.length > 0 ? { offered_option_keys: restored.map((o) => o.key) } : {}),
      ...(item.note ? { note: item.note } : {}),
    });
  }

  for (const role of roles) {
    if (!role.overridden) continue;

    rules.push({
      target_kind: 'role',
      target_key: role.role_key,
      access_mode: role.access_mode,
      // A Constraint Role is always in use, whatever a rule asks for; never write back a withdrawal
      // the resolver would refuse.
      ...(role.is_constraint ? {} : { withdrawn: !role.in_use }),
      ...(role.note ? { note: role.note } : {}),
    });
  }

  return rules;
}
